/**
 * The transactional outbox vocabulary (docs/EMAIL.md §6, docs/API.md).
 *
 * Domain events are written to `outbox_events` in the SAME transaction as
 * the ticket mutation, because there is no way to enqueue a BullMQ job
 * atomically with a Postgres commit, and a crash in between a resolution
 * commit and a queued notification means the requester silently never gets
 * their confirmation link.
 *
 * PAYLOADS CARRY IDS AND REFERENCES ONLY — NEVER TICKET DESCRIPTIONS, COMMENT
 * BODIES, RESOLUTION NOTES, EMAIL BODIES, OR CONFIRMATION TOKENS.
 *
 * `outbox_events` rows are long-lived (retried on failure, kept for
 * forensics) and widely readable (any service with DB access, any future
 * admin tooling, any ad-hoc debugging query). A confirmation token embedded
 * in a payload would be a bearer credential sitting at rest outside the
 * `email_action_tokens` table it belongs in — see docs/SECURITY.md §4, which
 * is exactly this same reasoning applied to the token itself. A description
 * or comment body embedded here duplicates content that already has its own
 * access-controlled home and bypasses `ticket_comments` visibility (public
 * vs internal) and RLS entirely. The dispatcher reads a payload and enqueues
 * a job that fetches its own content from the authoritative table at send
 * time — it never carries that content itself.
 *
 * Every payload schema below is `.strict()`, so an extra field — in
 * particular one of the forbidden ones above — fails validation instead of
 * being silently accepted or silently stripped.
 */
import { z } from 'zod';
import { TICKET_STATUSES } from './ticket-state-machine.js';
import { DESIGN_NOTIFY_EVENTS } from './design-workflow.js';
export const OUTBOX_EVENT_TYPES = {
    TICKET_CREATED: 'TICKET_CREATED',
    TICKET_ASSIGNED: 'TICKET_ASSIGNED',
    /** An incident report was submitted; the worker emails the configured recipients (content read from the report at send time). */
    INCIDENT_SUBMITTED: 'INCIDENT_SUBMITTED',
    /**
     * An Online Incident Management workflow step (assigned, RCA/CAPA submitted,
     * accepted, rejected, re-assigned, reopened, ...). Ids only; the worker
     * reads the case at send time and emails the people that step concerns.
     * `eventKey` is unique per step and makes the email idempotent.
     */
    INCIDENT_WORKFLOW: 'INCIDENT_WORKFLOW',
    /**
     * Incident SLA alert raised by the worker's incident SLA sweep: response SLA
     * breached, resolution due soon, or a resolution-SLA escalation (level 1-3
     * of the escalation matrix). `eventKey` is deterministic per alert.
     */
    INCIDENT_SLA_ALERT: 'INCIDENT_SLA_ALERT',
    /**
     * An Email Creation / Email Deletion ticket was created (same transaction as the ticket and
     * its user rows). The worker emails ONE consolidated request to the server-configured
     * recipient; the content is read from the stored ticket at send time (ids only here).
     */
    EMAIL_REQUEST_SUBMITTED: 'EMAIL_REQUEST_SUBMITTED',
    /**
     * An IT Invoice Submission reminder email is due (a scheduled slot claimed by
     * the worker, or an administrator's test send). The payload names the
     * `invoice_reminder_deliveries` row only; the worker reads the recipient and
     * builds the current-month summary at send time.
     */
    INVOICE_REMINDER_DUE: 'INVOICE_REMINDER_DUE',
    /**
     * A Design Workflow request changed stage (or was assigned). `notify` names
     * what happened; the worker resolves the recipients from the request and the
     * workflow configuration at send time (ids only here) and writes in-app
     * notifications + emails. `eventSeq` (the request's own counter) makes a
     * redelivery a no-op per recipient.
     */
    DESIGN_REQUEST_EVENT: 'DESIGN_REQUEST_EVENT',
    /**
     * A comment was posted (docs/API.md §5). The worker fans this out to the
     * requester and/or IT staff — never to anyone the comment's own visibility
     * would exclude, which is exactly why the payload carries `visibility` but
     * never `body`: the notification copy is rendered from the authoritative
     * `ticket_comments` row (and its RLS) at send time, not from this event.
     */
    COMMENT_CREATED: 'COMMENT_CREATED',
    /** Requester must be emailed a confirmation link — docs/API.md §4. */
    RESOLUTION_READY: 'RESOLUTION_READY',
    USER_CONFIRMATION_REQUESTED: 'USER_CONFIRMATION_REQUESTED',
    TICKET_REOPENED: 'TICKET_REOPENED',
    TICKET_CLOSED: 'TICKET_CLOSED',
    TICKET_RESOLUTION_REJECTED: 'TICKET_RESOLUTION_REJECTED',
    TICKET_ESCALATED: 'TICKET_ESCALATED',
    /** `RESOLVED`-free withdrawal — `TicketWorkflowService.cancel`. */
    TICKET_CANCELLED: 'TICKET_CANCELLED',
    /** Administrative `CANCELLED -> TRIAGED` restore — `TicketWorkflowService.restore`. */
    TICKET_RESTORED: 'TICKET_RESTORED',
    SLA_WARNING: 'SLA_WARNING',
    SLA_BREACHED: 'SLA_BREACHED',
    /**
     * A file was attached to a ticket (docs/API.md §5). Like `COMMENT_CREATED`,
     * the payload carries only identifiers — never the original filename, MIME
     * type or byte size, all of which stay in the authoritative
     * `ticket_attachments` row the worker re-reads at send time.
     */
    ATTACHMENT_ADDED: 'ATTACHMENT_ADDED',
    /** A file was soft-deleted from a ticket (`AttachmentService.softDelete`). */
    ATTACHMENT_REMOVED: 'ATTACHMENT_REMOVED',
};
const ticketStatusSchema = z.enum(TICKET_STATUSES);
const slaTargetKindSchema = z.enum(['FIRST_RESPONSE', 'RESOLUTION', 'CLOSURE']);
/** Every payload identifies its ticket the same two ways. */
const outboxBase = {
    ticketId: z.string().uuid(),
    ticketNumber: z.string(),
};
export const ticketCreatedPayloadSchema = z
    .object({
    ...outboxBase,
    requesterId: z.string().uuid(),
    createdAt: z.date(),
})
    .strict();
export const ticketAssignedPayloadSchema = z
    .object({
    ...outboxBase,
    actorId: z.string().uuid().nullable(),
    teamId: z.string().uuid().nullable(),
    technicianId: z.string().uuid().nullable(),
    previousTechnicianId: z.string().uuid().nullable(),
    assignedAt: z.date(),
    /**
     * How the assignment came about, so the notification can say who did it
     * ("by Sarah Williams", "by automatic routing", "when the ticket was
     * reopened"). Optional for events already queued before this field
     * existed; the worker treats an absent value as MANUAL. The assigner's
     * identity is never carried here: it is `actorId`, taken from the
     * authenticated session when the event is written.
     */
    assignedVia: z.enum(['MANUAL', 'CLAIM', 'AUTO_ROUTING', 'REOPEN_RETURN']).optional(),
    /**
     * Co-assignees ADDED by this assignment (`ticket_participants` role
     * ASSIGNEE). Present only when the request carried
     * `additionalTechnicianIds`; the worker notifies exactly these users in
     * addition to the lead. When present and the lead did not change, the
     * lead is not re-notified.
     */
    addedTechnicianIds: z.array(z.string().uuid()).optional(),
})
    .strict();
export const commentCreatedPayloadSchema = z
    .object({
    ...outboxBase,
    commentId: z.string().uuid(),
    /** Null for a SYSTEM-authored comment (e.g. a future automated note). */
    authorId: z.string().uuid().nullable(),
    visibility: z.enum(['PUBLIC', 'INTERNAL']),
    createdAt: z.date(),
})
    .strict();
export const resolutionReadyPayloadSchema = z
    .object({
    ...outboxBase,
    resolutionId: z.string().uuid(),
    attemptNo: z.number().int().positive(),
    recipientUserId: z.string().uuid(),
    resolvedAt: z.date(),
})
    .strict();
export const userConfirmationRequestedPayloadSchema = z
    .object({
    ...outboxBase,
    resolutionId: z.string().uuid(),
    attemptNo: z.number().int().positive(),
    recipientUserId: z.string().uuid(),
    requestedAt: z.date(),
})
    .strict();
export const ticketReopenedPayloadSchema = z
    .object({
    ...outboxBase,
    actorId: z.string().uuid(),
    resolutionId: z.string().uuid(),
    previousStatus: ticketStatusSchema,
    newStatus: ticketStatusSchema,
    reopenedAt: z.date(),
    reopenCount: z.number().int().positive(),
})
    .strict();
export const ticketClosedPayloadSchema = z
    .object({
    ...outboxBase,
    /** Null for the (rare) system-driven path; see docs C-2/D-02. */
    actorId: z.string().uuid().nullable(),
    resolutionId: z.string().uuid(),
    previousStatus: ticketStatusSchema,
    newStatus: ticketStatusSchema,
    closedAt: z.date(),
    /**
     * How the ticket came to be closed. The notification worker MUST branch on
     * this: telling a requester "thanks for confirming" when an administrator
     * force-closed their ticket, or when it auto-closed on their silence, is
     * both untrue and the exact conflation that AUTO_CONFIRMED and
     * ADMIN_OVERRIDE exist to prevent. Carrying it here rather than making the
     * worker re-read the ticket also keeps the event self-describing at the
     * moment it was emitted.
     */
    closureType: z.enum(['STANDARD', 'AUTO_CONFIRMED_TIMEOUT', 'ADMIN_OVERRIDE']),
})
    .strict();
export const ticketResolutionRejectedPayloadSchema = z
    .object({
    ...outboxBase,
    actorId: z.string().uuid(),
    resolutionId: z.string().uuid(),
    rejectedAt: z.date(),
})
    .strict();
export const ticketEscalatedPayloadSchema = z
    .object({
    ...outboxBase,
    /**
     * Nullable: a human escalation (`TicketWorkflowService.escalate`) always
     * carries the acting technician/lead's id, but an SLA-breach
     * auto-escalation (`apps/worker/src/jobs/sla-monitor.job.ts`) runs as the
     * system actor, which has no user id — mirrors `ticketAssignedPayloadSchema`
     * above for the identical "may be system-initiated" reason.
     */
    actorId: z.string().uuid().nullable(),
    fromTeamId: z.string().uuid().nullable(),
    toTeamId: z.string().uuid().nullable(),
    fromTechnicianId: z.string().uuid().nullable(),
    toTechnicianId: z.string().uuid().nullable(),
    escalatedAt: z.date(),
})
    .strict();
export const ticketCancelledPayloadSchema = z
    .object({
    ...outboxBase,
    actorId: z.string().uuid().nullable(),
    previousStatus: ticketStatusSchema,
    cancelledAt: z.date(),
})
    .strict();
export const ticketRestoredPayloadSchema = z
    .object({
    ...outboxBase,
    /** Always a real admin — `ticket:admin:restore` requires an authenticated actor. */
    actorId: z.string().uuid(),
    previousStatus: ticketStatusSchema,
    newStatus: ticketStatusSchema,
    restoredAt: z.date(),
})
    .strict();
export const slaWarningPayloadSchema = z
    .object({
    ...outboxBase,
    slaTargetKind: slaTargetKindSchema,
    dueAt: z.date(),
    evaluatedAt: z.date(),
})
    .strict();
export const slaBreachedPayloadSchema = z
    .object({
    ...outboxBase,
    slaTargetKind: slaTargetKindSchema,
    dueAt: z.date(),
    breachedAt: z.date(),
})
    .strict();
export const attachmentAddedPayloadSchema = z
    .object({
    ...outboxBase,
    attachmentId: z.string().uuid(),
    /** Null only if the uploader account is somehow absent by send time; the upload itself always requires an authenticated actor. */
    uploaderId: z.string().uuid().nullable(),
    createdAt: z.date(),
})
    .strict();
export const attachmentRemovedPayloadSchema = z
    .object({
    ...outboxBase,
    attachmentId: z.string().uuid(),
    actorId: z.string().uuid().nullable(),
    removedAt: z.date(),
})
    .strict();
/** One schema per event type, keyed so `buildOutboxEvent` stays type-safe. */
export const incidentSubmittedPayloadSchema = z
    .object({
    incidentId: z.string().uuid(),
    incidentNumber: z.string(),
    actorId: z.string().uuid().nullable(),
})
    .strict();
export const INCIDENT_WORKFLOW_EVENTS = [
    'ASSIGNED',
    'STAKEHOLDERS_ADDED',
    'RESPONSE_SUBMITTED',
    'ACCEPTED',
    'REJECTED',
    'REASSIGNED',
    'REOPENED',
];
export const incidentWorkflowPayloadSchema = z
    .object({
    incidentId: z.string().uuid(),
    incidentNumber: z.string(),
    event: z.enum(INCIDENT_WORKFLOW_EVENTS),
    actorId: z.string().uuid().nullable(),
    cycleNo: z.number().int().min(0),
    /** Newly added stakeholders for ASSIGNED / STAKEHOLDERS_ADDED / REASSIGNED / REOPENED (ids only). */
    stakeholderIds: z.array(z.string().uuid()).max(50),
    eventKey: z.string().uuid(),
})
    .strict();
export const INCIDENT_SLA_ALERT_KINDS = ['RESPONSE_BREACHED', 'RESOLUTION_DUE_SOON', 'ESCALATION'];
export const incidentSlaAlertPayloadSchema = z
    .object({
    incidentId: z.string().uuid(),
    incidentNumber: z.string(),
    kind: z.enum(INCIDENT_SLA_ALERT_KINDS),
    /** Escalation-matrix level (1-3) for ESCALATION; 0 otherwise. */
    level: z.number().int().min(0).max(3),
    cycleNo: z.number().int().min(0),
    eventKey: z.string().min(1).max(200),
})
    .strict();
export const EMAIL_REQUEST_KINDS = ['EMAIL_CREATION', 'EMAIL_DELETION'];
export const emailRequestSubmittedPayloadSchema = z
    .object({
    ...outboxBase,
    kind: z.enum(EMAIL_REQUEST_KINDS),
    actorId: z.string().uuid().nullable(),
})
    .strict();
export const invoiceReminderDuePayloadSchema = z
    .object({
    deliveryId: z.string().uuid(),
})
    .strict();
export const designRequestEventPayloadSchema = z
    .object({
    designRequestId: z.string().uuid(),
    requestNumber: z.string(),
    notify: z.enum(DESIGN_NOTIFY_EVENTS),
    eventSeq: z.number().int().nonnegative(),
    actorId: z.string().uuid().nullable(),
})
    .strict();
export const OUTBOX_PAYLOAD_SCHEMAS = {
    TICKET_CREATED: ticketCreatedPayloadSchema,
    TICKET_ASSIGNED: ticketAssignedPayloadSchema,
    INCIDENT_SUBMITTED: incidentSubmittedPayloadSchema,
    INCIDENT_WORKFLOW: incidentWorkflowPayloadSchema,
    INCIDENT_SLA_ALERT: incidentSlaAlertPayloadSchema,
    EMAIL_REQUEST_SUBMITTED: emailRequestSubmittedPayloadSchema,
    INVOICE_REMINDER_DUE: invoiceReminderDuePayloadSchema,
    DESIGN_REQUEST_EVENT: designRequestEventPayloadSchema,
    COMMENT_CREATED: commentCreatedPayloadSchema,
    RESOLUTION_READY: resolutionReadyPayloadSchema,
    USER_CONFIRMATION_REQUESTED: userConfirmationRequestedPayloadSchema,
    TICKET_REOPENED: ticketReopenedPayloadSchema,
    TICKET_CLOSED: ticketClosedPayloadSchema,
    TICKET_RESOLUTION_REJECTED: ticketResolutionRejectedPayloadSchema,
    TICKET_ESCALATED: ticketEscalatedPayloadSchema,
    TICKET_CANCELLED: ticketCancelledPayloadSchema,
    TICKET_RESTORED: ticketRestoredPayloadSchema,
    SLA_WARNING: slaWarningPayloadSchema,
    SLA_BREACHED: slaBreachedPayloadSchema,
    ATTACHMENT_ADDED: attachmentAddedPayloadSchema,
    ATTACHMENT_REMOVED: attachmentRemovedPayloadSchema,
};
/**
 * Validates `payload` against the schema for `type` and shapes the row the
 * caller writes into `outbox_events` in the same transaction as the ticket
 * mutation. Every event in this vocabulary is ticket-scoped, so
 * `aggregateType` is always `'Ticket'` and `aggregateId` is always the
 * validated `ticketId`.
 */
export function buildOutboxEvent(type, payload) {
    const schema = OUTBOX_PAYLOAD_SCHEMAS[type];
    const parsed = schema.parse(payload);
    if ('incidentId' in parsed) {
        return { aggregateType: 'Incident', aggregateId: parsed.incidentId, eventType: type, payload: parsed };
    }
    if ('designRequestId' in parsed) {
        return { aggregateType: 'DesignRequest', aggregateId: parsed.designRequestId, eventType: type, payload: parsed };
    }
    if ('deliveryId' in parsed) {
        return { aggregateType: 'InvoiceReminder', aggregateId: parsed.deliveryId, eventType: type, payload: parsed };
    }
    return {
        aggregateType: 'Ticket',
        aggregateId: parsed.ticketId,
        eventType: type,
        payload: parsed,
    };
}
//# sourceMappingURL=outbox-events.js.map